/**
 * First-run walkthrough — the pure half.
 *
 * A short, skippable coach-mark tour shown once when the app is first opened. It
 * points at the real controls in turn (the chart, the MOB button, the menu, the
 * favourites bar) with a line of plain teaching copy, so a brand-new skipper
 * knows where the important things live before they need them.
 *
 * The content and the little step/skip/seen state machine live here, away from
 * React and the DOM, so the whole flow can be unit-tested on its own: it
 * advances, it skips, it knows when it's on the last step, and it reports when
 * it's finished so the shell can remember the tour has been seen. The overlay
 * component in Walkthrough.tsx drives this and paints it.
 *
 * The copy is Edd's, verbatim — please don't reword it here.
 */

/** How a step is anchored on screen. */
export type StepPlacement =
  // A centred card, no spotlight — for the welcome and the "this is the chart"
  // beats, where the whole screen IS the thing we're talking about.
  | 'center'
  // A spotlight cut over the target element, with the card tucked beside it.
  // Falls back to a centred card if the target can't be found on screen.
  | 'target';

export interface TourStep {
  /** Stable id, handy for keys and tests. */
  id: string;
  /**
   * CSS selectors for the element this step points at, tried in order — the
   * first one found on screen wins. Empty for a centred step. A fallback
   * selector lets a step still land somewhere sensible when its first choice
   * isn't showing (e.g. the favourites bar only exists while the menu is open,
   * so it falls back to the menu button that opens it).
   */
  targets: string[];
  placement: StepPlacement;
  /** The teaching line shown on the card. Edd's voice, verbatim. */
  copy: string;
}

/**
 * The five beats, in order. Copy is verbatim and must not be reworded.
 */
export const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    targets: [],
    placement: 'center',
    copy: "Welcome aboard. This is your chart. Pinch to zoom, drag to move around, and everything you need sits round the edges. Give us ten seconds and we'll show you where.",
  },
  {
    id: 'mob',
    targets: ['.acp-mob-ctrl'],
    placement: 'target',
    copy: "This is the big one. If someone goes over the side, hit this. It marks the spot straight away and starts working out where they'll drift, so you can get back to them. Hope you never need it.",
  },
  {
    id: 'menu',
    targets: ['button[aria-label="Open the main menu"]'],
    placement: 'target',
    copy: "Everything else lives in here: charts, routes, tides, alarms, your boat's details. Have a poke about, you can't break anything.",
  },
  {
    id: 'chart',
    targets: [],
    placement: 'center',
    copy: "Press and hold anywhere on the chart to drop a mark or start a route, and hold on your own boat for its settings. Long-press is your friend, most of the quick stuff is just a hold away.",
  },
  {
    id: 'favourites',
    // The favourites bar only renders while the menu is open, so fall back to the
    // menu button that reveals it.
    targets: ['.acp-menu-favs', 'button[aria-label="Open the main menu"]'],
    placement: 'target',
    copy: "These are your shortcuts. The one on the left is whatever you use most, and you can add your own by holding any menu item. Right, you're sorted, off you go.",
  },
];

export const TOUR_STEP_COUNT = TOUR_STEPS.length;

/**
 * The tour's state, kept deliberately small:
 *  - running: is the overlay showing right now
 *  - step: which beat we're on (0-based) while running
 *  - completed: has the tour ended (finished or skipped) — the shell reads this
 *    to persist the "seen" flag so we never nag on the next launch.
 */
export interface TourState {
  running: boolean;
  step: number;
  completed: boolean;
}

/** The resting state before anything starts. */
export const IDLE_TOUR: TourState = { running: false, step: 0, completed: false };

/** Should the tour kick off automatically? Only when it's never been seen. */
export function shouldAutoStart(seen: boolean): boolean {
  return !seen;
}

/** Start (or replay) the tour from the first beat. */
export function startTour(): TourState {
  return { running: true, step: 0, completed: false };
}

/** True when we're on the final beat (so the card shows a Finish, not a Next). */
export function isLastStep(s: TourState): boolean {
  return s.running && s.step >= TOUR_STEP_COUNT - 1;
}

/**
 * Advance to the next beat. On the last beat this finishes the tour: it stops
 * running and marks completed, so the shell can remember it's been seen. A no-op
 * when the tour isn't running.
 */
export function nextStep(s: TourState): TourState {
  if (!s.running) return s;
  if (s.step >= TOUR_STEP_COUNT - 1) {
    return { running: false, step: TOUR_STEP_COUNT - 1, completed: true };
  }
  return { running: true, step: s.step + 1, completed: false };
}

/**
 * Skip out of the tour from wherever we are. Like finishing, this marks it
 * completed so it won't auto-run again. A no-op when it isn't running.
 */
export function skipTour(s: TourState): TourState {
  if (!s.running) return s;
  return { running: false, step: s.step, completed: true };
}

/** The current step's content while running, or null when it isn't. */
export function currentStep(s: TourState): TourStep | null {
  if (!s.running) return null;
  return TOUR_STEPS[s.step] ?? null;
}
